import { forwardRef } from 'react';

import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { styled, alpha } from '@mui/material/styles';
import ButtonBase from '@mui/material/ButtonBase';

import { useNavItem } from '../hooks';
import { Iconify } from '../../iconify';
import { navSectionClasses } from '../classes';
import { stateClasses, sharedStyles } from '../styles';

import type { NavItemProps, NavItemStateProps } from '../types';
// ----------------------------------------------------------------------

export const NavItem = forwardRef<HTMLButtonElement, NavItemProps>(
  (
    {
      path,
      icon,
      info,
      title,
      caption,
      //
      open,
      depth,
      render,
      active,
      disabled,
      hasChild,
      slotProps,
      externalLink,
      enabledRootRedirect,
      ...other
    },
    ref
  ) => {
    const navItem = useNavItem({
      path,
      icon,
      info,
      depth,
      render,
      hasChild,
      externalLink,
      enabledRootRedirect,
    });

    const rootItem = depth === 1;

    return (
      <Tooltip
        title={rootItem ? title : ''}
        placement={hasChild ? 'bottom' : 'right'}
        arrow
        disableHoverListener={hasChild}
        disableFocusListener={hasChild}
      >
        <StyledNavItem
          ref={ref}
          aria-label={title}
          depth={depth}
          active={active}
          disabled={disabled}
          open={open && !active}
          sx={{
            ...slotProps?.sx,
            [`& .${navSectionClasses.item.icon}`]: slotProps?.icon,
            [`& .${navSectionClasses.item.title}`]: slotProps?.title,
            [`& .${navSectionClasses.item.caption}`]: slotProps?.caption,
            [`& .${navSectionClasses.item.info}`]: slotProps?.info,
            [`& .${navSectionClasses.item.arrow}`]: slotProps?.arrow,
          }}
          className={stateClasses({ open: open && !active, active, disabled })}
          {...navItem.baseProps}
          {...other}
        >
          {icon && (
            <Box component="span" className={navSectionClasses.item.icon}>
              {navItem.renderIcon}
            </Box>
          )}

          {/* Show text only in popover sub-items, not in collapsed root sidebar */}
          {title && !rootItem && (
            <Box
              component="span"
              className={navSectionClasses.item.title}
              sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: '1 1 auto' }}
            >
              {title}
            </Box>
          )}

          {caption && (
            <Tooltip title={caption} arrow placement="right">
              <Iconify icon="eva:info-outline" className={navSectionClasses.item.caption} />
            </Tooltip>
          )}

          {info && navItem.subItem && (
            <Box component="span" className={navSectionClasses.item.info}>
              {navItem.renderInfo}
            </Box>
          )}

          {hasChild && (
            <Iconify
              icon="eva:arrow-ios-forward-fill"
              className={navSectionClasses.item.arrow}
              sx={{
                width: 10,
                height: 10,
                marginLeft: '16px !important',
                opacity: 0.7,
              }}
            />
          )}
        </StyledNavItem>
      </Tooltip>
    );
  }
);

NavItem.displayName = 'NavItem';

// ----------------------------------------------------------------------

const StyledNavItem = styled(ButtonBase, {
  shouldForwardProp: (prop) =>
    prop !== 'active' && prop !== 'open' && prop !== 'disabled' && prop !== 'depth',
})<NavItemStateProps>(({ active, open, disabled, depth, theme }) => {
  const rootItem = depth === 1;

  const subItem = !rootItem;

  const baseStyles = {
    item: {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      borderRadius: 'var(--nav-item-radius)',
      color: theme.palette.text.primary,
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
    title: {},
    caption: {
      width: 16,
      height: 16,
      color: theme.palette.text.primary,
    },
    icon: {
      ...sharedStyles.icon,
      width: 'var(--nav-icon-size)',
      height: 'var(--nav-icon-size)',
      color: theme.palette.text.primary,
    },
    arrow: {
      ...sharedStyles.arrow,
      color: theme.palette.text.primary,
    },
    info: { ...sharedStyles.info },
  } as const;

  return {
    /**
     * Root item
     */
    ...(rootItem && {
      ...baseStyles.item,
      textAlign: 'center',
      flexDirection: 'column',
      minHeight: 'var(--nav-item-root-height)',
      padding: 'var(--nav-item-root-padding)',
      width: 'var(--nav-item-root-width)',
      maxWidth: 'var(--nav-item-root-max-width)',
      boxSizing: 'border-box',
      [`& .${navSectionClasses.item.icon}`]: {
        ...baseStyles.icon,
        margin: 'var(--nav-icon-root-margin)',
      },
      [`& .${navSectionClasses.item.title}`]: {
        ...baseStyles.title,
        ...sharedStyles.noWrap,
        lineHeight: '16px',
        fontSize: {
          xs: theme.typography.pxToRem(8),
          sm: theme.typography.pxToRem(9),
          md: theme.typography.pxToRem(9),
        },
        fontWeight: active ? theme.typography.fontWeightBold : theme.typography.fontWeightSemiBold,
        maxWidth: '100%',
        width: '100%',
        textAlign: 'center',
      },
      [`& .${navSectionClasses.item.caption}`]: {
        ...baseStyles.caption,
        top: 11,
        left: 6,
        position: 'absolute',
      },
      [`& .${navSectionClasses.item.arrow}`]: {
        ...baseStyles.arrow,
        top: 11,
        right: 6,
        position: 'absolute',
      },
      [`& .${navSectionClasses.item.info}`]: { ...baseStyles.info },
      // State
      ...(active && {
        color: theme.palette.text.primary,
        backgroundColor: alpha(theme.palette.grey[500], 0.08),
        borderRadius: 'var(--nav-item-radius)',
        '&:hover': {
          backgroundColor: alpha(theme.palette.grey[500], 0.12),
        },
        [`& .${navSectionClasses.item.icon}`]: {
          ...baseStyles.icon,
          color: theme.palette.primary.main,
        },
        [`& .${navSectionClasses.item.title}`]: {
          ...baseStyles.title,
          ...sharedStyles.noWrap,
          color: theme.palette.text.primary,
          maxWidth: '100%',
          width: '100%',
          textAlign: 'center',
          fontWeight: theme.typography.fontWeightBold,
        },
        [`& .${navSectionClasses.item.arrow}`]: {
          ...baseStyles.arrow,
          top: 11,
          right: 6,
          position: 'absolute',
          color: theme.palette.primary.main,
        },
      }),
      ...(open && {
        color: 'var(--nav-item-root-open-color)',
        backgroundColor: 'var(--nav-item-root-open-bg)',
      }),
    }),

    /**
     * Sub item
     */
    ...(subItem && {
      ...baseStyles.item,
      minHeight: 'var(--nav-item-sub-height)',
      padding: 'var(--nav-item-sub-padding)',
      [`& .${navSectionClasses.item.icon}`]: {
        ...baseStyles.icon,
        margin: 'var(--nav-icon-sub-margin)',
      },
      [`& .${navSectionClasses.item.title}`]: {
        ...baseStyles.title,
        ...theme.typography.body2,
        fontWeight: active
          ? theme.typography.fontWeightSemiBold
          : theme.typography.fontWeightMedium,
        flex: '1 1 auto',
      },
      [`& .${navSectionClasses.item.caption}`]: { ...baseStyles.caption },
      [`& .${navSectionClasses.item.arrow}`]: {
        ...baseStyles.arrow,
        marginRight: theme.spacing(-0.5),
      },
      [`& .${navSectionClasses.item.info}`]: { ...baseStyles.info },
      // State
      ...(active && {
        color: theme.palette.text.primary,
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
        },
        [`& .${navSectionClasses.item.icon}`]: {
          ...baseStyles.icon,
          margin: 'var(--nav-icon-sub-margin)',
          color: theme.palette.primary.main,
        },
        [`& .${navSectionClasses.item.title}`]: {
          ...baseStyles.title,
          ...sharedStyles.noWrap,
          color: theme.palette.text.primary,
          flex: '1 1 auto',
          fontWeight: theme.typography.fontWeightSemiBold,
        },
        [`& .${navSectionClasses.item.arrow}`]: {
          ...baseStyles.arrow,
          top: 11,
          right: 6,
          position: 'absolute',
          color: theme.palette.primary.main,
        },
      }),
      ...(open && {
        color: 'var(--nav-item-sub-open-color)',
        backgroundColor: 'var(--nav-item-sub-open-bg)',
      }),
    }),

    /* Disabled */
    ...(disabled && sharedStyles.disabled),
  };
});
